// Internal infrastructure shared by every `Session` implementation. The
// fetch/invalidate side of a Session is impl-specific (in-memory Map vs
// TanStack-Query cache); the bindings side — getBinding / recordBinding /
// bindingNames / exportRecipes + reactive subscribe — is identical across
// impls. Folded out here so `createInMemorySession` (core) and
// `createChatSession` (plugin-chat) share one source of truth for the
// binding semantics, the snapshot-stability contract, and the subscriber
// notification timing.
//
// **Bindings are recipe-backed and immutable between resets.** A binding
// is not an opaque evaluated `Value` — its identity is the s-expression
// that produced it (`Recipe.exprAst`). The `Value` map is just a cache of
// the evaluated recipe; the ordered `recipes` log is the source of truth.
// This makes "reference a dataset across tool calls" and "rebuild the
// session" the *same* operation — replay the recipe — rather than two
// code paths. Bindings are immutable (rebinding a live name to a
// different expression throws `SessionRebindingError`) so a dashboard
// tile holding an unresolved `(col <name> …)` can never have its meaning
// retroactively changed by a later binding. The host does not persist
// the recipe log directly: the chat host derives the recipe sequence
// from the active message branch and uses `resetBindings()` +
// recipe replay to keep the namespace a projection of the transcript
// (no parallel persisted store to drift on regenerate / reload).

import { hashKey } from '@tanstack/react-query';

import { NO_POS } from '../ast.ts';
import { SessionRebindingError, ShadowingOpError } from '../errors.ts';
import type { AstInput } from '../normalize.ts';
import type { Value } from '../value.ts';

/**
 * A persisted binding: the name plus the *unevaluated* producing
 * expression (the bare `(query-knowledge-graph …)` / `(transform …)`
 * AST — NOT wrapped in `(dataset …)`). `exprAst` is plain JSON data by
 * the engine's normalize invariant (source positions live in a sidecar),
 * so a recipe round-trips through `JSON.stringify` and is cheaply
 * reconstructable from the producing `expression` carried in the
 * persisted message transcript (the chat host projects recipes from
 * the active branch rather than persisting a parallel log). Replay
 * rebuilds a cheap `LazyDataset` — never materialised rows; CDF I/O
 * still routes through `session.fetch` on demand exactly as in the
 * live session.
 */
export interface Recipe {
  readonly name: string;
  readonly exprAst: AstInput;
}

/**
 * Reconstruct the top-level form that, when evaluated, reproduces a
 * binding: `(dataset <name> <exprAst>)`. SSOT for the replay shape —
 * used by hosts rebuilding a session by replaying its recipe sequence
 * (the chat host projects that sequence from the active message
 * branch). Pure (no evaluator import) so it stays cycle-safe.
 */
export const recipeToForm = (recipe: Recipe): AstInput => ({
  name: 'dataset',
  // biome-ignore lint/style/useNamingConvention: $-prefixed engine reserved
  params: [{ $sym: recipe.name }, recipe.exprAst],
});

/**
 * Conversation-scoped binding store. The shape mirrors the bindings-
 * related slice of the `Session` interface: every `Session` impl spreads
 * one of these into its returned object alongside its own fetch /
 * invalidate implementations. Consumers reach this surface through the
 * `Session` contract, not directly — the type is exported only so
 * impls can compose it.
 *
 * @see Session — the public interface this contributes to.
 * @see createInMemorySession — composes this with a Map-backed cache.
 * @see createChatSession (plugin-chat) — composes this with a
 *      QueryClient-backed cache.
 */
export interface BindingStore {
  readonly getBinding: (name: string) => Value | undefined;
  /**
   * Record an immutable binding. Validates the name (op-name shadowing
   * → `ShadowingOpError`; already-bound → `SessionRebindingError`),
   * appends `{ name, exprAst }` to the ordered recipe log, and caches
   * the evaluated `Value`. Both engine-internal lexical bindings (the
   * `(dataset …)` op) and direct host/tool bindings route through here,
   * so the validation and the recipe log are a single source of truth.
   */
  readonly recordBinding: (name: string, exprAst: AstInput, value: Value) => void;
  readonly bindingNames: () => readonly string[];
  /**
   * The ordered recipe log (append-only *between* `resetBindings()`) —
   * the serialisable replay form. Replaying these via `recipeToForm`
   * in order against a fresh/empty namespace reconstructs every
   * binding; dependency order is preserved because creation order is
   * (a recipe can only reference earlier bindings). Hosts replay this
   * to rebuild a session; the chat host derives the sequence from the
   * active message branch rather than persisting it separately.
   */
  readonly exportRecipes: () => readonly Recipe[];
  /**
   * Drop every binding and reset the recipe log to empty, then notify
   * subscribers. The store returns to its just-constructed state so a
   * caller can re-project a fresh set of bindings into it.
   *
   * This is the one sanctioned escape from binding immutability: it
   * exists because the conversation's bindings are a *projection* of
   * the active message branch, and a branch divergence (regenerate /
   * edit) replaces — not extends — that branch. Recording is still
   * append-only and rebind-strict between resets; only a whole-branch
   * change clears the namespace so the surviving branch can be
   * re-projected without stale-recipe collisions.
   */
  readonly resetBindings: () => void;
  readonly subscribe: (listener: () => void) => () => void;
}

/**
 * Build a fresh binding store. Each call returns an independent
 * instance (separate recipe log, separate value cache, separate
 * listener set).
 *
 * `reservedNames` is the registered-op name set; binding a name in it
 * throws `ShadowingOpError` (parity with the engine's lexical
 * `checkBindable`, so the direct host/tool path and the `(dataset …)`
 * op path reject identically). Defaults to empty for non-React callers
 * (tests, CLI) that never bind op names.
 *
 * Behaviors implemented here:
 *
 * - **Immutable but idempotent, recipe-backed bindings.**
 *   `recordBinding` rejects rebinding a name to a *different*
 *   expression (`SessionRebindingError`) so prior `(col <name> …)`
 *   references stay stable; re-recording the *same* recipe
 *   (structurally equal `exprAst` via TanStack's `hashKey`) is a no-op
 *   success — required for re-render / double-invoke / retry /
 *   projection-replay idempotency. The ordered `recipes` log is the
 *   replay source of truth; `bindings` is its evaluated cache.
 *   `resetBindings()` clears both for a whole-branch re-projection.
 * - **Reference-stable `bindingNames` snapshot.** The returned array is
 *   cached and invalidated only when a new name is added. (No removal
 *   path exists — bindings are immutable.) This is what makes the
 *   `useBindingNames` hook bail out via `Object.is`.
 * - **Synchronous listener notification on every `recordBinding`.**
 *   Subscribers fire synchronously after the recipe log + value cache
 *   are updated and the snapshot invalidated, so a listener that
 *   immediately reads `bindingNames()` / `exportRecipes()` observes
 *   the new state (the chat host's branch reconciler + `useBindingNames`
 *   rely on this).
 * - **Iterate-a-copy on notify.** A listener that subscribes or
 *   unsubscribes itself or another listener during a notification
 *   does not corrupt the live iteration.
 * - **Set-based listener dedup.** Subscribing the same listener twice
 *   triggers it once; the returned unsubscriber is idempotent.
 */
export const createBindingStore = (reservedNames: ReadonlySet<string> = new Set()): BindingStore => {
  const bindings = new Map<string, Value>();
  const recipes: Recipe[] = [];
  const listeners = new Set<() => void>();
  let snapshot: readonly string[] | null = null;

  const notify = (): void => {
    // `notify` fires on a genuine key-set change: a genuinely new
    // binding (idempotent re-records and rejected rebinds return/throw
    // before reaching here) or a `resetBindings` clear. Either way the
    // snapshot is stale — invalidate it.
    snapshot = null;
    // Iterate a copy so a listener that unsubscribes itself (or another
    // listener) during the notification doesn't corrupt the live Set.
    [...listeners].forEach((listener) => {
      listener();
    });
  };

  return {
    getBinding: (name) => bindings.get(name),
    recordBinding: (name, exprAst, value) => {
      if (reservedNames.has(name)) {
        throw new ShadowingOpError(name, NO_POS);
      }
      const existing = recipes.find((r) => r.name === name);
      if (existing !== undefined) {
        // Immutable, but idempotent. Re-recording the *same* recipe for
        // a name — React re-render / StrictMode double-invoke / agent
        // retry / hydration replay re-running a producer — is a no-op
        // success, NOT an error. Only a *different* expression for an
        // already-bound name is rejected: that would retroactively
        // change every prior `(col <name> …)` reference (e.g. a
        // dashboard tile resolved at render time). Recipe identity uses
        // TanStack's `hashKey` — the same deterministic, sorted-key
        // structural serialisation it uses for query-key equality — so
        // we don't hand-roll a deep-equal (AstInput is JSON-safe: the
        // parser emits no Date/Dataset literals).
        if (hashKey([existing.exprAst]) === hashKey([exprAst])) {
          return;
        }
        // The store owns the live name set, so it — not the caller —
        // computes a concretely-free alternative (`name_2`, `name_3`, …)
        // and hands it to the error. The agent then recovers in one
        // step instead of blindly suffixing and re-colliding.
        const taken = new Set(recipes.map((r) => r.name));
        let suffix = 2;
        while (taken.has(`${name}_${suffix}`)) {
          suffix += 1;
        }
        throw new SessionRebindingError(name, `${name}_${suffix}`);
      }
      bindings.set(name, value);
      recipes.push({ name, exprAst });
      notify();
    },
    bindingNames: () => {
      snapshot ??= Array.from(bindings.keys());
      return snapshot;
    },
    exportRecipes: () => recipes,
    resetBindings: () => {
      // Whole-branch replacement: drop the value cache + recipe log so
      // the surviving branch can be re-projected from scratch. `recipes`
      // is a const array — clear it in place rather than rebind.
      bindings.clear();
      recipes.length = 0;
      notify();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};
